<template>
	<div v-if="availableLanguages.length > 1" class="mob-nav-language-switch" ref="switchRef">
		<!-- 触发器 -->
		<div class="mob-nav-language-switch-trigger" @click="toggleDropdown">
			<div class="mob-nav-language-switch-item">
				{{ currentLanguageLabel }}
			</div>
			<SvgIcons
				name="surf_arrow_up"
				class="mob-nav-language-switch-arrow"
				:class="{ 'arrow-rotated': dropdownVisible }"
			/>
		</div>

		<!-- 语言下拉列表 -->
		<transition name="dropdown-fade">
			<div v-show="dropdownVisible" class="mob-nav-language-dropdown">
				<div
					v-for="lang in availableLanguages"
					:key="lang.key"
					class="mob-nav-language-dropdown-item"
					:class="{ 'active': lang.key === currentLanguage }"
					@click="handleLangChange(lang.key)"
				>
					<div class="mob-nav-language-dropdown-item-content">
						{{ lang.value }}
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script setup>
import { useLanguageSwitch } from '@rebuild/materials/shared/floating/hooks/useLanguageSwitch.js';
import i18n, { EN, ZH, ZH_HK } from '@/locales';
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import SvgIcons from '@/components/SvgIcons.vue';
import { useCommonData } from '@/hooks';

const { enabledLangList, changeLocaleLang } = useLanguageSwitch();

const switchRef = ref(null);
const dropdownVisible = ref(false);
const toggleDropdown = () => {
	dropdownVisible.value = !dropdownVisible.value;
};
const closeDropdown = () => {
	dropdownVisible.value = false;
};

// 点击外部判断逻辑
const handleClickOutside = event => {
	if (switchRef.value && !switchRef.value.contains(event.target)) {
		closeDropdown();
	}
};
onMounted(() => {
	document.addEventListener('click', handleClickOutside);
	window.addEventListener('scroll', closeDropdown, true);
});
onBeforeUnmount(() => {
	document.removeEventListener('click', handleClickOutside);
	window.removeEventListener('scroll', closeDropdown, true);
});

// 语言标签映射
const langLabelMap = {
	[ZH]: '中文',
	[ZH_HK]: '繁体',
	[EN]: 'English'
};
const langLabelMapShort = {
	[ZH]: '中',
	[ZH_HK]: '繁',
	[EN]: 'En'
};
const sortRule = [ZH, ZH_HK, EN];

const currentLanguage = computed(() => i18n.locale);

// 当前语言标签
const currentLanguageLabel = computed(() => {
	return langLabelMapShort[currentLanguage.value] || langLabelMapShort[ZH];
});

// 可用语言列表（包含所有语言，不过滤当前语言）
const availableLanguages = computed(() => {
	const enabledLanguages = enabledLangList.value;
	return enabledLanguages
		.sort((a, b) => sortRule.indexOf(a) - sortRule.indexOf(b))
		.map(key => ({
			key,
			value: langLabelMap[key]
		}));
});

const { viewInIframe } = useCommonData();
const handleLangChange = lang => {
	if (viewInIframe.value) return;
	changeLocaleLang(lang);
	closeDropdown();
};
</script>

<style lang="less">
// 过渡动画
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
	transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
	opacity: 0;
	transform: translateY(-4px);
}

.dropdown-fade-enter-to,
.dropdown-fade-leave-from {
	opacity: 1;
	transform: translateY(0);
}

// 下拉框样式
.mob-nav-language-dropdown {
	position: absolute;
	top: calc(100% + 3px);
	right: 0;
	z-index: calc(var(--surf-nav-z-index) + 1);
	padding: 0;
	min-width: 100px;
	border-radius: 2px;
	border: 0.5px solid rgba(var(--surf-primary-color-rgb), 0.3);
	background: var(--surf-secondary-color);
	box-shadow: 0 10px 20px 0 rgba(var(--surf-primary-color-rgb), 0.4);

	.mob-nav-language-dropdown-item {
		position: relative;
		cursor: pointer;
		padding: 4px;

		.mob-nav-language-dropdown-item-content {
			padding: 7px 12px;
			color: rgba(var(--surf-inverse-color-rgb), 0.6);
			font-size: 14px;
			font-weight: 400;
			line-height: 18px;
			display: flex;
			align-items: center;
			justify-content: flex-start;
			transition: all 0.3s;
		}

		&:hover,
		&.active {
			.mob-nav-language-dropdown-item-content {
				color: var(--surf-inverse-color);
			}
		}
	}
}
</style>
<style lang="less" scoped>
.mob-nav-language-switch {
	position: relative;

	.mob-nav-language-switch-trigger {
		display: flex;
		align-items: center;
		cursor: pointer;
		transition: all 0.3s;
	}

	.mob-nav-language-switch-item {
		color: var(--surf-inverse-color);
		font-size: 16px;
		font-weight: 600;
		line-height: 20px;
	}

	.mob-nav-language-switch-arrow {
		font-size: 20px;
		color: var(--surf-inverse-color) !important;
		transform: rotate(180deg);
		transition: transform 0.3s ease;

		&.arrow-rotated {
			transform: rotate(0deg);
		}
	}
}
</style>
